import { TenantConfig } from '@app/models/tenant.model'
import { environment } from '@env/environment'
import { Injectable, signal } from '@angular/core'
import { HttpClient, HttpErrorResponse } from '@angular/common/http'
import { firstValueFrom } from 'rxjs'

@Injectable({ providedIn: 'root' })
export class TenantConfigService {
    readonly config = signal<TenantConfig | null>(null)
    /**
     * Página de erro (`/error/<rota>`) quando o `/tenant-config.json` falha. A navegação fica com o
     * `tenantAccessGuard`: navegar daqui, durante o APP_INITIALIZER, perde para a navegação inicial.
     */
    readonly errorRoute = signal<string | null>(null)

    constructor(private readonly http: HttpClient) {
    }

    async load(): Promise<void> {
        if (!environment.production) {
            this.config.set({
                tenantSlug: 'local',
                tenantId: environment.tenantId,
                apiBaseUrl: environment.urlApi,
                name: null
            })
            return
        }

        try {
            const config = await firstValueFrom(
                this.http.get<TenantConfig>('/tenant-config.json', {
                    headers: {
                        'Cache-Control': 'no-cache'
                    }
                })
            )

            this.config.set(config)
        } catch (error) {
            console.error('Failed to load tenant-config.json', error)

            this.errorRoute.set(tenantErrorRoute(error instanceof HttpErrorResponse ? error.status : 0))
        }
    }

    get requiredConfig(): TenantConfig {
        const current = this.config()

        if (!current) {
            throw new Error('Tenant config not loaded')
        }

        return current
    }
}

/** Status do `/tenant-config.json` → rota em `/error`. 404: o subdomínio não é de nenhum ente. */
export function tenantErrorRoute(status: number): string {
    switch (status) {
        case 400:
            return '400'
        case 401:
            return '401'
        case 403:
            return '403'
        case 404:
            return '404'
        case 503:
            return '503'
        case 504:
            return '504'
        default:
            return '500'
    }
}
